import axios from 'axios';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import ReactTable from '../../../../components/ReactTable';
import { IoClose } from 'react-icons/io5';
import { FiCheck, FiEye, FiLoader } from 'react-icons/fi'; // Import FiLoader for the spinner
import { createColumnHelper } from '@tanstack/react-table';
import { CiEdit } from 'react-icons/ci';
import { GoTrash } from "react-icons/go";
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';

function HandbookCategoryList() {
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false); // To track loading state
  const [searchQuery, setSearchQuery] = useState('');
  const [showEntries, setShowEntries] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);
  const [isModalOpen, setIsModalOpen] = useState(false);  // State for modal visibility
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false); // State for delete confirmation modal
  const [categoryToDelete, setCategoryToDelete] = useState(null); // State to store the category to delete
  const [newCategory, setNewCategory] = useState({ title: '' });  // State for new category form
  const [editCategory, setEditCategory] = useState(null); // State to handle editing

  // Get the token from the Redux store
  const { token ,authToken} = useSelector((state) => state.login);

  const columnHelper = createColumnHelper();

  const base = `${process.env.REACT_APP_BASE_URL}/${process.env.REACT_APP_API_TYPE}`;

  // Fetch categories from the API with pagination
  const fetchPublicCollaterals = async (page = 1, limit = 10, query = '') => {
    setLoading(true); // Set loading to true before API call
    try {
      const response = await axios.get(`${base}/learningHandbooks`, {
        headers: {
          'Authorization': `Bearer ${authToken || token}`,  // Add the token to the request header
        },
      });

      setCategories(response?.data?.data?.list);
      setTotalPages(response.data.totalPages);  // Set total pages from the response
    } catch (error) {
      console.error('Error fetching bonds:', error);
    } finally {
      setLoading(false); // Set loading to false after the API call is complete
    }
  };

  // Handle new category form input change
  const handleCategoryInputChange = (e) => {
    const { name, value } = e.target;
    setNewCategory((prev) => ({ ...prev, [name]: value }));
  };

  // Handle new category form submission
  const handleCreateCategory = async () => {
    if (newCategory.title === "") {
      toast.info("Please add category");
      return;
    }
    try {
      const response = await axios.post(
        `${base}/learningHandbooks`,
        { title: newCategory.title },
        {
          headers: {
            Authorization: `Bearer ${authToken || token}`,
          },
        }
      );
      fetchPublicCollaterals();
      setIsModalOpen(false); // Close the modal after creation
      setNewCategory({ title: '' }); // Reset the form data
    } catch (error) {
      console.error('Error creating category:', error);
    }
  };

  // Handle delete category click
  const handleDeletecategory = async () => {
    try {
      if (categoryToDelete) {
        await axios.delete(`${base}/learningHandbooks/${categoryToDelete}`, {
          headers: {
            Authorization: `Bearer ${authToken || token}`,
          },
        });
        fetchPublicCollaterals();
        setIsDeleteModalOpen(false); // Close delete confirmation modal
        setCategoryToDelete(null); // Reset the category to delete
      }
    } catch (error) {
      console.error('Error deleting category:', error);
    }
  };

  // Handle edit category click (open edit modal)
  const handleEditCategory = (category) => {
    setEditCategory(category);
    setIsModalOpen(true); // Open modal to edit category
  };

  // Handle category edit input change
  const handleEditCategoryInputChange = (e) => {
    const { name, value } = e.target;
    setEditCategory((prev) => ({ ...prev, [name]: value }));
  };

  // Handle category edit form submission
  const handleEditCategorySubmit = async () => {
    if (editCategory.title === "") {
      toast.info("Please add category");
      return;
    }
    try {
      const response = await axios.put(
        `${base}/learningHandbooks/${editCategory.id}`,
        { title: editCategory.title },
        {
          headers: {
            Authorization: `Bearer ${authToken || token}`,
          },
        }
      );
      fetchPublicCollaterals();
      setIsModalOpen(false); // Close modal after update
      setEditCategory(null); // Reset edit form data
    } catch (error) {
      console.error('Error editing category:', error);
    }
  };

  // Reset the modal form when closed
  const handleCloseModal = () => {
    setIsModalOpen(false);
    setNewCategory({ title: '' }); // Reset the new category data
    setEditCategory(null); // Reset the edit category data
  };

  useEffect(() => {
    fetchPublicCollaterals(currentPage, showEntries, searchQuery);  // Fetch categories when component mounts
  }, [currentPage, showEntries, searchQuery]);

  const hostedcolumns = [
    columnHelper.accessor((row) => row.title, {
      id: 'categoryTitle', // Unique ID for this column
      cell: (info) => info.renderValue(),
      header: () => <span>Category Title</span>,
    }),

    columnHelper.accessor((row) => row, {
      id: 'action',
      cell: (info) => {
        const category = info?.row?.original;

        return (
          <div className="flex justify-end gap-5">
            <span className='cursor-pointer' onClick={(e) => handleNavigate(category.id)}>
              <FiEye />
            </span>
            <span className='cursor-pointer' onClick={() => handleEditCategory(category)}>
              <CiEdit />
            </span>
            <span className='cursor-pointer' onClick={() => { setCategoryToDelete(category.id); setIsDeleteModalOpen(true); }}>
              <GoTrash />
            </span>
          </div>
        );
      },
      header: () => (
        <div className="flex justify-end">
          <span>Action</span>
        </div>
      ),
    }),
  ];

  const handleNavigate = (id) => {
    navigate(`/manage-resource/training/handbook/${id}`)
  }

  // Loader Component using React Icons FiLoader
  const Loader = () => (
    <div className="flex justify-center items-center">
      <FiLoader className="animate-spin text-4xl text-blue-600" />
    </div>
  );

  return (
    <div className="p-6 bg-gray-50 rounded-lg mt-[50px]">
      <div className="flex justify-between mb-6">
        <h2 className="text-2xl font-semibold text-gray-800">Learning Handbook Category List</h2>
        <div className="flex items-center space-x-2">
          <button
            onClick={() => setIsModalOpen(true)} // Open modal for creating new category
            className="bg-[#065697] text-white p-3 rounded-md transition"
          >
            Add New Category
          </button>
        </div>
      </div>

      {/* Show Loader when loading is true */}
      {loading ? (
        <Loader />
      ) : (
        <div>
          {/* Table rendering */}
          {categories?.length > 0 && (
            <ReactTable key={JSON.stringify(categories)} defaultData={categories} columns={hostedcolumns} />
          )}
        </div>
      )}

      {/* New Category Modal */}
      {(isModalOpen || editCategory) && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex justify-center items-center">
          <div className="bg-white p-6 rounded-lg w-1/3">
            <div className="flex justify-between items-center">
              <h3 className="text-xl font-semibold">
                {editCategory ? 'Edit Category' : 'Create New Category'}
              </h3>
              <IoClose
                className="cursor-pointer"
                onClick={handleCloseModal} // Close modal and reset form
              />
            </div>
            <div className="mt-4">
              <input
                type="text"
                name="title"
                value={editCategory ? editCategory.title : newCategory.title}
                onChange={editCategory ? handleEditCategoryInputChange : handleCategoryInputChange}
                placeholder="Category Title"
                className="p-3 border rounded-md w-full"
              />
            </div>
            <div className="mt-4 flex justify-end">
              <button
                onClick={editCategory ? handleEditCategorySubmit : handleCreateCategory}
                disabled={loading} // Disable the button during loading
                className="bg-[#065697] text-white p-3 rounded-md transition"
              >
                {editCategory ? 'Update' : 'Create'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {isDeleteModalOpen && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex justify-center items-center">
          <div onClick={() => setIsDeleteModalOpen(false)} className="bg-white p-6 rounded-lg w-1/3">
            <h3 className="text-xl font-semibold">Are you sure you want to delete this category?</h3>
            <div className="mt-4 flex justify-end gap-4">
              <button
                onClick={() => setIsDeleteModalOpen(false)}
                className="bg-gray-300 text-black p-2 rounded-md"
              >
                Cancel
              </button>
              <button
                onClick={handleDeletecategory}
                disabled={loading} // Disable the button during loading
                className="bg-red-600 text-white p-2 rounded-md"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default HandbookCategoryList;
